/**
 * Render entry and node-normalization utilities for Smoothie UI.
 *
 * These helpers feed the patch-runtime pipeline directly.
 *
 * @module @smoothie-framework/ui/render/render-entry
 */
import { createNodeId } from './node-id';
import { createPatchEmitter } from './emitter';
import { createDOMPatchApplier } from './patch-dom';
import { createReactiveTextNode, createTextNode, ensureNodeId, isUINode as isRuntimeUINode, isSignalLike, } from './child-nodes';
import { createRuntimeComponentNode, createRuntimeComponentRender } from './runtime-component';
export function render(template, container, options = {}) {
    const node = normalizeToNode(template);
    if (!options.preserveExistingContent) {
        container.textContent = '';
    }
    const applier = createDOMPatchApplier(container, {
        debug: options.debugPatches,
        schemaValidation: options.schemaValidation,
        preserveExistingContent: options.preserveExistingContent,
    });
    const emitter = createPatchEmitter({
        schedule: options.schedule,
        now: options.now,
    });
    emitter.mount(node, (patches, meta) => applier.apply(patches, meta));
    return () => {
        emitter.dispose();
        applier.dispose();
        container.textContent = '';
    };
}
/**
 * Normalize various renderable types to UINode
 */
export function normalizeToNode(value) {
    const createNode = (type, props, children, text, key) => ({
        id: createNodeId(),
        type,
        props,
        children,
        text,
        key,
    });
    const createFragment = (children = []) => (createNode('Fragment', {}, children));
    const createText = (text) => (createTextNode(text));
    // Handle primitives
    if (value === null || value === undefined) {
        return createFragment();
    }
    if (typeof value === 'string') {
        return createText(value);
    }
    if (typeof value === 'number') {
        return createText(String(value));
    }
    if (typeof value === 'boolean') {
        return createFragment();
    }
    // Already a UINode
    if (isRuntimeUINode(value)) {
        return ensureNodeId(value);
    }
    // Keep signal accessors on the same reactive text path that the JSX runtime
    // already uses for raw intrinsic children.
    if (isSignalLike(value)) {
        return createReactiveTextNode(value);
    }
    // Component template - execute it
    if (typeof value === 'function') {
        const template = value;
        return createRuntimeComponentNode({
            id: createNodeId(),
            ownerName: 'AnonymousTemplate',
            component: template,
            render: createRuntimeComponentRender({
                ownerName: 'AnonymousTemplate',
                render: () => normalizeToNode(template()),
                normalize: (result) => result,
            }),
        });
    }
    // Array of children
    if (Array.isArray(value)) {
        return createFragment(value.map((v) => normalizeToNode(v)));
    }
    // Fallback
    return createText(String(value));
}
/**
 * Flatten children array, removing nulls and undefined
 */
export function flattenChildren(children) {
    const result = [];
    for (const child of children) {
        if (child === null || child === undefined)
            continue;
        if (typeof child === 'boolean')
            continue;
        const node = normalizeToNode(child);
        // Unwrap static fragments, but preserve runtime component fragments so
        // they can materialize later in the render pipeline.
        if (node.type === 'Fragment' && !node._runtimeRender) {
            result.push(...node.children);
        }
        else {
            result.push(node);
        }
    }
    return result;
}
/**
 * Type guard for UINode
 */
export function isUINode(value) {
    return isRuntimeUINode(value);
}
//# sourceMappingURL=render-entry.js.map